import type { FC } from "react";

import { Typography } from "@ui/components/Typography";
import classNames from "classnames";

import { AshIcon } from "@jti/brand-icons/ash";
import { CreditCardBlockedIcon } from "@jti/brand-icons/credit-card-blocked";
import { DeviceBluetoothIcon } from "@jti/brand-icons/device-bluetooth";
import { EditIcon } from "@jti/brand-icons/edit";
import { Gen4AutoOnIcon } from "@jti/brand-icons/gen4-auto-on";
import { GiftIcon } from "@jti/brand-icons/gift";
import { IngredientsFibersIcon } from "@jti/brand-icons/ingredients-fibers";
import { IngredientsFlavoringIcon } from "@jti/brand-icons/ingredients-flavoring";
import { IngredientsGumbaseIcon } from "@jti/brand-icons/ingredients-gumbase";
import { IngredientsNicotineIcon } from "@jti/brand-icons/ingredients-nicotine";
import { LockOutlineCloseIcon } from "@jti/brand-icons/lock-outline-close";
import { MouthTingleIcon } from "@jti/brand-icons/mouth-tingle";
import { MouthUnderLipIcon } from "@jti/brand-icons/mouth-under-lip";
import { NoiseAndSmokeIcon } from "@jti/brand-icons/noise-and-smoke";
import { PackageIcon } from "@jti/brand-icons/package";
import { PackageArrowIcon } from "@jti/brand-icons/package-arrow";
import { PackageCheckIcon } from "@jti/brand-icons/package-check";
import { PackageReturnIcon } from "@jti/brand-icons/package-return";
import { PackageShoppingIcon } from "@jti/brand-icons/package-shopping";
import { PackageTimeIcon } from "@jti/brand-icons/package-time";
import { PinTimePlaceIcon } from "@jti/brand-icons/pin-time&place";
import { PlusFallingIcon } from "@jti/brand-icons/plus-falling";
import { PouchDisposeIcon } from "@jti/brand-icons/pouch-dispose";
import { ProfileDefaultIcon } from "@jti/brand-icons/profile-default";
import { ProfileFamilyIcon } from "@jti/brand-icons/profile-family";
import { ProfileGearIcon } from "@jti/brand-icons/profile-gear";
import { SmokeIcon } from "@jti/brand-icons/smoke";
import { StarsShinningIcon } from "@jti/brand-icons/stars-shinning";
import { TimerIcon } from "@jti/brand-icons/timer";
import { TobaccoLeafIcon } from "@jti/brand-icons/tobacco-leaf";
import { ToolsNoToolIcon } from "@jti/brand-icons/tools-no_tool";
import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { UiCheckplusCircleIcon } from "@jti/brand-icons/ui-checkplus-circle";
import { ZeroTobaccoIcon } from "@jti/brand-icons/zero-tobacco";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./EcommerceReassuranceBanner.module.scss";

export const icons = {
  account: <ProfileDefaultIcon />,
  cardWithLock: <CreditCardBlockedIcon />,
  checkMarkWithRoundBorder: <UiCheckCircleIcon />,
  checkPlusCircle: <UiCheckplusCircleIcon />,
  easyToMaintain: <ToolsNoToolIcon />,
  edit: <EditIcon />,
  falling: <PlusFallingIcon />,
  ingredientFibers: <IngredientsFibersIcon />,
  ingredientFlavoring: <IngredientsFlavoringIcon />,
  ingredientGumbase: <IngredientsGumbaseIcon />,
  ingredientNicotine: <IngredientsNicotineIcon />,
  lockOutlineClose: <LockOutlineCloseIcon />,
  minimalCleaning: <StarsShinningIcon />,
  mouthTingle: <MouthTingleIcon />,
  mouthUnderLip: <MouthUnderLipIcon />,
  noAsh: <AshIcon />,
  noSmokeSmell: <NoiseAndSmokeIcon />,
  package: <PackageIcon />,
  packageReturn: <PackageReturnIcon />,
  packageShopping: <PackageShoppingIcon />,
  packageWithArrow: <PackageArrowIcon />,
  packageWithCheckMark: <PackageCheckIcon />,
  packageWithClock: <PackageTimeIcon />,
  pinTimePlace: <PinTimePlaceIcon />,
  pleasantAroma: <SmokeIcon />,
  pouchDispose: <PouchDisposeIcon />,
  profileFamily: <ProfileFamilyIcon />,
  timer: <TimerIcon />,
  tobaccoLeaf: <TobaccoLeafIcon />,
  autoOnIcon: <Gen4AutoOnIcon />,
  bluetooth: <DeviceBluetoothIcon />,
  localRewardIcon: <GiftIcon />,
  profileGearIcon: <ProfileGearIcon />,
  zeroTobacco: <ZeroTobaccoIcon />,
} as const;

export type IconKey = keyof typeof icons;

export type EcommerceReassuranceBannerItem = {
  id: string;
  iconName: IconKey;
  description: string;
  href?: string;
};

export type EcommerceReassuranceBannerProps = {
  items: EcommerceReassuranceBannerItem[];
  title?: string;
  theme?: "light" | "dark";
};

export const EcommerceReassuranceBanner: FC<EcommerceReassuranceBannerProps> = ({ title, items, theme }) => {
  const themeClass = {
    light: "light-theme",
    dark: "dark-theme",
  };
  return (
    <div className={styles.wrapper}>
      {title && (
        <Typography className={styles.title} variant="h2">
          {title}
        </Typography>
      )}
      <ul className={classNames(styles.container, theme ? themeClass[theme] : undefined)}>
        {items.map(({ description, iconName, id, href }) => (
          <li key={id} className={classNames(styles.item)}>
            <span className={styles.iconWrapper} data-testid="ecommerceReassuranceBanner-icon">
              {icons[iconName]}
            </span>

            <span className={styles.description}>{description}</span>

            {href && <LinkBehaviour href={href} className={styles.link} />}
          </li>
        ))}
      </ul>
    </div>
  );
};
